import React, {useEffect, useState} from "react";
import { useNavigate } from "react-router-dom";
import noAvatar from "../../assets/img/noAvatarAdmin.svg";
import noAvatarText from "../../assets/img/noAvatarAdminText.svg";
import ConfirmPopup from "./ConfirmPopup";
import {currentUserActions} from "reducers/currentUser";
import {useDispatch, useSelector} from "react-redux";
import {ThunkDispatch} from "@reduxjs/toolkit";
import { formatName } from "utils/format";
import CustomToast from "components/CustomToast";
import {useSnackbar} from "notistack";
import AvatarDialog from "../pages/super_user/components/AvatarDialog";
import { setSuperUserLogged, updateSuperUserAvatar, setSuccess, setError } from "reducers/superUser";
import LogoHeader from "components/LogoHeader";

const SuperUserHeader = () => {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
    const navigate = useNavigate();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { superUserInfo, superUserToast } = useSelector((state: any) => state.currentUser);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [optionsOpen, setOptionsOpen] = useState(false);
    const { superUserLogged, success, error, actionName } = useSelector( (state: any) => state.superUser);

    useEffect(() => {
        dispatch(setSuperUserLogged(superUserInfo));
    }, [superUserInfo]);

    useEffect(() => {
        if(superUserToast?.message) {
            showToast('メッセージ', superUserToast.message, superUserToast.variant);
        }
    }, [superUserToast]);

    const showToast = (title: string, message: string, variant: string) => {
        enqueueSnackbar("I love snacks.", {
            content: (key) => (<CustomToast title={title} message={message} variant={variant} snackbarKey={key} closeSnackbar={closeSnackbar} />),
        });
    };

    const signOutUser = () => {
        dispatch(currentUserActions.signOutUser({navigate, view: 'control'}))
    };

    const userOptions = [
        {
            name: "管理ユーザーとしてログイン",
            onClick: () => navigate("/control/selection"),
        },
    ];

    const handleAvatarChange = (file: any) => {
        if (!file && !superUserLogged?.delete_avatar) {
            setIsConfirmOpen(false);
            return;
        }
        if(superUserLogged){
            const data = {
                id: superUserLogged.id,
                formData: {
                    avatar: file,
                    delete_avatar: false,
                },
            };
            dispatch(updateSuperUserAvatar(data));
        }
        setDialogOpen(false);
    };

    const handleDeleteCurrentAvatar = () => {
        setIsConfirmOpen(false);
        if(superUserLogged){
            const data = {
                id: superUserLogged.id,
                formData: {
                    avatar: null,
                    delete_avatar: true,
                },
            };
            dispatch(updateSuperUserAvatar(data));
        }
    }

    const handleBlur = (event: any) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setOptionsOpen(false);
        }
    };

    const handleOpenAvatarDialog = (e: any) => {
        setDialogOpen(true);
        e.stopPropagation();
    }

    useEffect(() => {
        if(success && actionName == "updateSuperUserAvatar"){
            enqueueSnackbar("I love snacks.", {
                content: (key) => (
                    <CustomToast
                        title={"メッセージ"}
                        message={success}
                        variant={"success"}
                        snackbarKey={key}
                        closeSnackbar={closeSnackbar}
                    />
                ),
            });
            const newData = {
                ...superUserInfo,
                avatar: superUserLogged.avatar,
            }
            dispatch(currentUserActions.setSuperUserInfo(newData));
            dispatch(setSuccess(null));
        }
        else if(error && actionName == "updateSuperUserAvatar") {
            enqueueSnackbar("I love snacks.", {
                content: (key) => (
                    <CustomToast
                        title={"メッセージ"}
                        message={error}
                        variant={"error"}
                        snackbarKey={key}
                        closeSnackbar={closeSnackbar}
                    />
                ),
            });
            dispatch(setError(null));
        }
    }, [enqueueSnackbar, success, error, actionName]);

    return (
        <>
        {dialogOpen && (
            <AvatarDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onConfirm={handleAvatarChange}
            onConfirmPopupOpen={() => setIsConfirmOpen(true)}
            />
        )}
        {isConfirmOpen && (
            <ConfirmPopup
            open={isConfirmOpen}
            onClose={() => setIsConfirmOpen(false)}
            confirmContent={"現在使用しているプロフィール画像を削除しますか？"}
            onConfirm={handleDeleteCurrentAvatar}
            />
        )}
        <div className="px-4 md:px-6 py-2 bg-white">
            <div className="flex items-center justify-between">
                <div className="md:visible"><LogoHeader view="control"></LogoHeader></div>
                <div
                    onBlur={handleBlur}
                    tabIndex={-1}
                    className="flex items-center relative cursor-pointer"
                    onClick={() => setOptionsOpen((prev) => !prev)}
                >
                    {optionsOpen && (
                        <ul
                            className="z-[99] min-w-[15rem] rounded absolute top-[calc(100%+8px)] right-0 py-2 bg-white alu-options-box-shadow"
                        >
                        {userOptions.map((option, idx) => (
                            <li
                                tabIndex={0}
                                key={idx}
                                className="px-4 py-2 rounded flex items-center gap-2 hover:bg-[#FF861A]/10 hover:cursor-pointer"
                                onClick={() => option?.onClick()}
                            >
                                <p className="text-base alu-text-base-2">
                                    {option.name}
                                </p>
                            </li>
                        ))}
                        </ul>
                    )}
                    <div className="w-8 h-8 relative mr-2" onClick={(e) => {handleOpenAvatarDialog(e)}}>
                        <img
                            src={superUserInfo?.avatar ? superUserInfo?.avatar : noAvatar}
                            alt="profile"
                            className="w-full h-full rounded-full object-cover"
                            />
                        {!superUserInfo?.avatar && <img src={noAvatarText} alt="" className="absolute left-0 bottom-0 w-4 h-2" />}
                    </div>
                    <h2 className="hidden md:block text-[0.875rem] font-normal leading-6 alu-text-base-2">{formatName(superUserInfo?.first_name, superUserInfo?.last_name)}</h2>
                    <button className="hidden md:block">
                        <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        >
                        <g clipPath="url(#clip0_8574_176)">
                            <path d="M7 10L12 15L17 10H7Z" fill="#343A3D"/>
                        </g>
                        <defs>
                            <clipPath id="clip0_8574_176">
                            <rect width="24" height="24" fill="white"/>
                            </clipPath>
                        </defs>
                        </svg>
                    </button>
                </div>
            </div>
        </div>
        </>
    );
};

export default SuperUserHeader;
